import React from 'react'

import styled from 'styled-components'

import { Avatar } from 'src/Components/Avatar'
import { variables } from 'src/Styled/variables'
import { LikeDislikeButtonProps } from 'src/Components/LikeDislikeBlock'
import { Review } from 'src/Types/reviews'
import { RateInfo } from 'src/Components/RateInfo'
import {
  CampaignReview,
  ReviewTypeStatus,
} from 'src/Hooks/useCampaignDetailsReviews'

import { AddedPlaylistInfo } from '../AddedPlaylistInfo'
import { MatchedPlaylists } from '../MatchedPlaylistsInfo'
import { ReviewType } from '../ReviewType'

import { ReviewActions } from './ReviewActions'
import { ReviewCommentSection } from './ReviewCommentSection'
import { formatCuratorStats } from './helpers'

const Container = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  border-radius: 4px;
  background: ${({ theme }) => theme.color.white};
`

const CuratorInfoBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  align-self: stretch;
`

const MiddleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
`

const CuratorInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
`

export const AccentText = styled.span`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight600};
  line-height: 150%;
`

const SubText = styled.span`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
  word-break: break-word;
  @media screen and (min-width: 767px) and (max-width: 800px) {
    max-width: 146px;
  }
`

const StyledReviewType = styled(ReviewType)`
  position: absolute;
  top: 0;
  right: 0;
`

export interface ReviewPlaylist {
  playlistName: string
  playlistUrl?: string
  playlistCoverUrl?: string
}

export interface ReviewBlockProps {
  userCover: string
  reviewId: number
  firstName: string
  lastName: string
  playlistsCount: number
  followersCount: number
  comment?: string
  addedTrackPlaylist?: ReviewPlaylist | null
  matchedTrackPlaylists?: CampaignReview['matchedTrackPlaylists']
  onShareClick: () => void
  onSubmitLikeDislike?: LikeDislikeButtonProps['onSubmitLikeDislike']
  reviewComplain?: Review['artist_feedback_status']
  vocalRating?: number
  melodyRating?: number
  productionRating?: number
  isFavouriteCurator?: boolean
  onClickSave?: () => void
  onAddFavoriteCuratorLoading: boolean
  reviewStatus?: ReviewTypeStatus
}

export const ReviewBlock: React.FC<ReviewBlockProps> = ({
  userCover,
  firstName,
  lastName,
  playlistsCount,
  followersCount,
  comment,
  addedTrackPlaylist,
  matchedTrackPlaylists,
  onShareClick,
  onSubmitLikeDislike,
  reviewId,
  reviewComplain,
  vocalRating,
  melodyRating,
  productionRating,
  isFavouriteCurator,
  onClickSave,
  onAddFavoriteCuratorLoading,
  reviewStatus,
}) => {
  const curatorStats = formatCuratorStats(playlistsCount, followersCount)

  const isSkippedReview = reviewStatus === 'notReviewed'
  const isNotReviewed =
    reviewStatus === 'awaiting' || reviewStatus === 'notReviewed'

  return (
    <Container>
      {reviewStatus && <StyledReviewType status={reviewStatus} />}
      <CuratorInfoBlock>
        <Avatar width={40} height={40} src={userCover} />

        <CuratorInfo>
          <AccentText>
            {[firstName, lastName].filter(Boolean).join(' ')}
          </AccentText>
          {curatorStats && <SubText>{curatorStats}</SubText>}
        </CuratorInfo>
      </CuratorInfoBlock>

      <MiddleBlock>
        <ReviewCommentSection comment={comment} />
        {!isNotReviewed && (
          <RateInfo
            productionValue={productionRating}
            vocalValue={vocalRating}
            melodyValue={melodyRating}
          />
        )}
      </MiddleBlock>

      {addedTrackPlaylist && (
        <AddedPlaylistInfo
          playlistName={addedTrackPlaylist.playlistName}
          playlistUrl={addedTrackPlaylist.playlistUrl}
          playlistCoverUrl={addedTrackPlaylist.playlistCoverUrl}
        />
      )}
      {matchedTrackPlaylists && !addedTrackPlaylist && !isSkippedReview && (
        <MatchedPlaylists matchedTrackPlaylists={matchedTrackPlaylists} />
      )}

      <ReviewActions
        reviewStatus={reviewStatus}
        onSubmitLikeDislike={onSubmitLikeDislike}
        reviewId={reviewId}
        reviewComplain={reviewComplain}
        onShareClick={onShareClick}
        onClickSave={onClickSave}
        isFavouriteCurator={isFavouriteCurator}
        onAddFavoriteCuratorLoading={onAddFavoriteCuratorLoading}
      />
    </Container>
  )
}
